import React, { useContext, useState } from 'react';
import {
  NativeBaseProvider, VStack, Text, HStack,
} from 'native-base';
import {
  Button, Dialog, Divider, IconButton, List, Portal, Searchbar, TextInput,
} from 'react-native-paper';
import { ScrollView } from 'react-native';
import { useMutation, useQuery } from '@apollo/client';
import { useToast } from 'react-native-toast-notifications';
import { ModalContext } from '../../context/ModalContext';
import {
  CREATE_DRIVER_GROUP,
  UPDATE_DRIVER_GROUP,
  ADD_DRIVER_TO_GROUP,
  REMOVE_DRIVER_FROM_GROUP,
} from '../../gql/driver-group/mutation';
import { GET_DRIVER_GROUPS } from '../../gql/driver-group/query';
import { GET_DRIVERS } from '../../gql/user/query';

function ManageDriverGroup() {
  const toast = useToast();
  const { displayManageDriverGroupModal, toggleManageDriverGroupModal } = useContext(ModalContext);
  const { title, driverGroup: initialGroup, onSave } = displayManageDriverGroupModal;

  const isEditing = !!initialGroup;

  const [name, setName] = useState(initialGroup?.name ?? '');
  const [description, setDescription] = useState(initialGroup?.description ?? '');
  const [memberSearch, setMemberSearch] = useState('');

  // Live query so member list updates immediately after add/remove
  const { data: groupsData } = useQuery(GET_DRIVER_GROUPS, { skip: !isEditing });
  const liveGroup = isEditing
    ? (groupsData?.driverGroups ?? []).find((g) => g.id === initialGroup.id) ?? initialGroup
    : null;

  const { data: driversData } = useQuery(GET_DRIVERS);
  const [createDriverGroup, { loading: creating }] = useMutation(CREATE_DRIVER_GROUP, {
    onCompleted: () => {
      toast.show('Groupe créé avec succès.');
      if (onSave) onSave();
      toggleManageDriverGroupModal();
    },
    onError: (err) => toast.show(err.message),
    refetchQueries: [{ query: GET_DRIVER_GROUPS }],
  });
  const [updateDriverGroup, { loading: updating }] = useMutation(UPDATE_DRIVER_GROUP, {
    onCompleted: () => {
      toast.show('Groupe mis à jour.');
      if (onSave) onSave();
      toggleManageDriverGroupModal();
    },
    onError: (err) => toast.show(err.message),
    refetchQueries: [{ query: GET_DRIVER_GROUPS }],
  });
  const [addDriverToGroup] = useMutation(ADD_DRIVER_TO_GROUP, {
    onError: (err) => toast.show(err.message),
    refetchQueries: [{ query: GET_DRIVER_GROUPS }],
  });
  const [removeDriverFromGroup] = useMutation(REMOVE_DRIVER_FROM_GROUP, {
    onError: (err) => toast.show(err.message),
    refetchQueries: [{ query: GET_DRIVER_GROUPS }],
  });

  const eligibleDrivers = (driversData?.allUsers ?? []).filter(
    (u) => u.role === 'COACH' || u.role === 'SUPERADMIN' || u.status === 'COMPANION',
  );

  const currentMemberIds = new Set((liveGroup?.members ?? []).map((m) => m.userId));

  const filteredDrivers = eligibleDrivers.filter((u) => {
    if (!memberSearch) return true;
    const q = memberSearch.toLowerCase();
    return `${u.firstName} ${u.lastName}`.toLowerCase().includes(q);
  });

  const handleSave = () => {
    if (!name.trim()) {
      toast.show('Le nom est requis.');
      return;
    }
    if (isEditing) {
      updateDriverGroup({ variables: { id: initialGroup.id, name: name.trim(), description: description || null } });
    } else {
      createDriverGroup({ variables: { name: name.trim(), description: description || null } });
    }
  };

  const isLoading = creating || updating;

  return (
    <Dialog
      visible={displayManageDriverGroupModal.display}
      dismissable
      onDismiss={toggleManageDriverGroupModal}
      style={{ maxHeight: '85%' }}
    >
      <Dialog.Title>{title}</Dialog.Title>
      <Dialog.ScrollArea style={{ maxHeight: 500 }}>
        <ScrollView contentContainerStyle={{ paddingVertical: 8 }}>
          <VStack space={3} px={1}>
            <TextInput
              label="Nom du groupe"
              mode="outlined"
              value={name}
              onChangeText={setName}
            />
            <TextInput
              label="Description (optionnel)"
              mode="outlined"
              value={description}
              onChangeText={setDescription}
            />

            {isEditing && (
              <>
                <Divider style={{ marginVertical: 4 }} />
                <Text fontSize="md" fontWeight="bold">Membres</Text>

                {(liveGroup?.members ?? []).map((m) => (
                  <HStack key={m.userId} alignItems="center" justifyContent="space-between">
                    <VStack flex={1}>
                      <Text>{m.user.firstName} {m.user.lastName}</Text>
                      <Text fontSize="xs" color="gray.500">{m.user.role} · {m.user.status}</Text>
                    </VStack>
                    <IconButton
                      icon="minus-circle"
                      size={20}
                      onPress={() => removeDriverFromGroup({
                        variables: { groupId: initialGroup.id, userId: m.userId },
                      })}
                    />
                  </HStack>
                ))}

                <Divider style={{ marginVertical: 4 }} />
                <Text fontSize="sm" color="gray.500">Ajouter un conducteur</Text>
                <Searchbar
                  placeholder="Rechercher un conducteur"
                  value={memberSearch}
                  onChangeText={setMemberSearch}
                  style={{ elevation: 0, borderWidth: 1, borderColor: '#ccc' }}
                />
                {filteredDrivers
                  .filter((u) => !currentMemberIds.has(u.id))
                  .map((u) => (
                    <List.Item
                      key={u.id}
                      title={`${u.firstName} ${u.lastName}`}
                      description={`${u.role} · ${u.status}`}
                      right={(props) => (
                        <IconButton
                          {...props}
                          icon="plus-circle"
                          size={20}
                          onPress={() => addDriverToGroup({
                            variables: { groupId: initialGroup.id, userId: u.id },
                          })}
                        />
                      )}
                    />
                  ))}
              </>
            )}
          </VStack>
        </ScrollView>
      </Dialog.ScrollArea>
      <Dialog.Actions>
        <Button onPress={toggleManageDriverGroupModal}>Annuler</Button>
        <Button mode="contained" onPress={handleSave} loading={isLoading} disabled={isLoading}>
          Sauvegarder
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}

export default function ManageDriverGroupModal() {
  const { displayManageDriverGroupModal } = useContext(ModalContext);
  if (!displayManageDriverGroupModal.display) return null;
  return (
    <Portal>
      <NativeBaseProvider>
        <ManageDriverGroup />
      </NativeBaseProvider>
    </Portal>
  );
}
